import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Bot, FolderCode, RefreshCw, Sparkles } from "lucide-react";
import ChatComposer from "../components/ChatComposer";
import ChatMessage from "../components/ChatMessage";
import Loader from "../components/Loader";
import { useApplicationsContext } from "../context/ApplicationsContext";
import { useAuth } from "../context/AuthContext";
import useAgentChat from "../hooks/useAgentChat";

const SUGGESTIONS = [
  "ساختار این پروژه را برایم توضیح بده.",
  "چه بخش‌هایی از کد اخیراً تغییر کرده است؟",
  "یک صفحه جدید به این اپلیکیشن اضافه کن.",
  "تست‌های این پروژه را اجرا کن و نتیجه را بگو.",
];

export default function ApplicationChat() {
  const { applicationId } = useParams();
  const { applications, loading, error } = useApplicationsContext();
  const { user } = useAuth();
  const { messages, pending, send, reset } = useAgentChat();
  const scrollRef = useRef(null);

  const application = applications.find((item) => String(item.id) === applicationId);

  // Switching applications starts a fresh conversation.
  useEffect(() => {
    reset();
  }, [applicationId, reset]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, pending]);

  if (loading) {
    return <Loader message="در حال بارگذاری اپلیکیشن…" />;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  if (!application) {
    return (
      <div className="card state">
        <div className="state-icon" aria-hidden="true">
          <FolderCode size={30} strokeWidth={1.7} />
        </div>
        <p>این اپلیکیشن پیدا نشد.</p>
        <Link className="btn btn-ghost" to="/dashboard">
          بازگشت به داشبورد
        </Link>
      </div>
    );
  }

  const userInitial = (user?.full_name || user?.username || "؟").trim().charAt(0);

  return (
    <div className="chat">
      <header className="chat-head card">
        <div className="chat-head-main">
          <span className="app-icon" aria-hidden="true">
            <FolderCode size={20} strokeWidth={1.8} />
          </span>
          <div>
            <h1>{application.title}</h1>
            <code className="app-path">{application.path}</code>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={reset}
          disabled={messages.length === 0}
        >
          <RefreshCw size={16} strokeWidth={1.9} aria-hidden="true" />
          گفت‌وگوی جدید
        </button>
      </header>

      <div className="chat-body card">
        <div className="chat-scroll" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="chat-empty">
              <span className="chat-empty-icon" aria-hidden="true">
                <Sparkles size={26} strokeWidth={1.8} />
              </span>
              <h2>با ایجنت «{application.title}» گفت‌وگو کنید</h2>
              <p>
                بنویسید می‌خواهید روی این اپلیکیشن چه کاری انجام شود. برای شروع می‌توانید یکی از
                نمونه‌های زیر را انتخاب کنید.
              </p>
              <div className="chat-suggestions">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="suggestion"
                    onClick={() => send(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="chat-messages">
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} userInitial={userInitial} />
              ))}

              {pending && (
                <div className="msg msg-agent">
                  <span className="msg-avatar" aria-hidden="true">
                    <Bot size={18} strokeWidth={1.9} />
                  </span>
                  <div className="msg-body">
                    <div className="msg-meta">
                      <strong>ایجنت</strong>
                    </div>
                    <div className="msg-bubble typing" aria-label="در حال نوشتن">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <ChatComposer onSend={send} disabled={pending} />
      </div>
    </div>
  );
}
